import { createContext, useContext } from 'react';
import type {
  CareerDetailResponse,
  CareerSummaryResponse,
  CourseDetailResponse,
  CourseSummaryResponse,
  LearnPostResponse,
  LearnPostSummary,
  LessonResponse,
  NavItemKey,
  PageResponse,
  ProblemDetailResponse,
  ProblemSummaryResponse,
  PublicStatsResponse,
  TopicCountResponse,
} from '@/api/types';
import type { Lang } from '@/types';
import { isHydrating } from './hydration';

/**
 * Prerender vaqtida API dan olingan ma'lumot. Server shu bilan render qiladi va
 * HTML ga `<script id="__SQLM_DATA__">` sifatida qo'shadi — client hydration'da
 * aynan shu ma'lumot bilan birinchi renderni qiladi (qayta fetch va "sakrash"siz).
 */
export interface InitialData {
  /** Qaysi sahifa uchun (pathname) */
  path: string;
  lang: Lang;
  /** 404 sahifasi — client ham NotFound ni render qiladi */
  notFound?: boolean;
  problem?: ProblemDetailResponse;
  /** Shu mavzudagi keyingi qiyinlikdagi masalalar */
  related?: ProblemSummaryResponse[];
  /** /problems — birinchi sahifa */
  problems?: PageResponse<ProblemSummaryResponse>;
  /** /topics/:topic, /problems/difficulty/:level — to'plamdagi barcha masalalar */
  collection?: ProblemSummaryResponse[];
  topicCounts?: TopicCountResponse[];
  stats?: PublicStatsResponse;
  /** /courses */
  courses?: CourseSummaryResponse[];
  careers?: CareerSummaryResponse[];
  /** /careers/:slug */
  career?: CareerDetailResponse;
  /** /courses/:slug va dars sahifasi (dastur — yon panel) */
  course?: CourseDetailResponse;
  /** /courses/:slug/:lesson — anonim ko'rinish (qulflangan darsda video/konspekt yo'q) */
  lesson?: LessonResponse;
  /** /learn — barcha nashr qilingan postlar; /topics/:topic — shu mavzudagi postlar */
  learnPosts?: LearnPostSummary[];
  /** /learn/:slug — anonim ko'rinish (pullik postda matn yo'q) */
  learnPost?: LearnPostResponse;
  /** Admin yashirgan menyu bandlari — har bir sahifada (Navbar/Footer) */
  navHidden?: NavItemKey[];
}

export const INITIAL_DATA_ELEMENT_ID = '__SQLM_DATA__';

const InitialDataContext = createContext<InitialData | null>(null);

export const InitialDataProvider = InitialDataContext.Provider;

/** Client: HTML ichidagi JSON ni o'qiydi (bo'lmasa null). */
export function readInitialData(): InitialData | null {
  const el = document.getElementById(INITIAL_DATA_ELEMENT_ID);
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent) as InitialData;
  } catch {
    return null;
  }
}

/**
 * Prerender ma'lumoti — faqat hydration (birinchi) renderida qaytadi. Keyingi
 * SPA navigatsiyalarda null — sahifa odatdagidek API dan yuklaydi.
 * Faqat useState initializer ichida ishlatilsin.
 */
export function useInitialData(): InitialData | null {
  const data = useContext(InitialDataContext);
  return data && isHydrating() ? data : null;
}
